import { Box, Button, CircularProgress, MenuItem, Stack, Tooltip, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { useTheme } from '@mui/material/styles';
import { enqueueSnackbar } from "notistack";
import DisplayErrors from "src/components/display-errors";
import Iconify from "src/components/iconify/iconify";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxTextField from "src/components/inputs/trux-text-field";
import SimpleDialog from "src/components/simple-dialog";
import { ModelSavingData } from "src/hooks/use-model-saving-data";
import { EldVehicle, EldVehicleStats } from "src/models";
import TruxCheckboxField from "src/components/inputs/trux-checkbox-field";
import { Constants } from "src/constants";
import TruxSimpleDecimalTwoField from "src/components/inputs/trux-simple-decimal-two-field";
import TruxSelectField from "src/components/inputs/trux-select-field";
import { Violations } from "src/components/violations";
import { useAuthContext } from "src/auth/hooks";

type EldVehicleModalProps = {
  selectedEldVehicleModel: ModelSavingData<EldVehicle>,
  isOpen: boolean,
  setIsOpen: Dispatch<SetStateAction<boolean>>,
  eldVehicleStats: EldVehicleStats[],
  updateEldVehicle: VoidFunction
}

function validateVIN(vin: string): boolean {
  vin = vin.toUpperCase();
  if (vin.length !== 17 && vin.length !== 18) {
      return false;
  }

  const weights: number[] = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
  let checkDigit = 0;

  const vinValues: { [key: string]: number } = {
      '0': 0, '1': 1, '2': 2, '3': 3, '4': 4,
      '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
      'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5,
      'F': 6, 'G': 7, 'H': 8, 'J': 1, 'K': 2,
      'L': 3, 'M': 4, 'N': 5, 'P': 7, 'R': 9,
      'S': 2, 'T': 3, 'U': 4, 'V': 5, 'W': 6,
      'X': 7, 'Y': 8, 'Z': 9
  };

  for (let i = 0; i < 17; i+=1) {
      const char = vin[i];
      const value = vinValues[char];

      if (value === undefined) {
          return false; // Invalid character in VIN
      }

      checkDigit += value * weights[i];
  }

  checkDigit %= 11;
  if (checkDigit === 10) {
      return vin[8] === 'X';
  }

  return checkDigit === parseInt(vin[8], 10);
}

export function EldVehicleModal(
  { selectedEldVehicleModel, eldVehicleStats, isOpen, setIsOpen, updateEldVehicle }: EldVehicleModalProps
) {
  const stats = eldVehicleStats?.find(s => s.eldVehicleId === selectedEldVehicleModel?.model?.id);

  const [loading, setLoading] = useState(false);

  const [loadingVin, setLoadingVin] = useState(false);

  const decodeVin = () => {
    setLoadingVin(true);
    const eldVehicle = selectedEldVehicleModel.model;
    if (eldVehicle) {
      if (!eldVehicle.vin) {
        enqueueSnackbar("Please enter a vin", { variant: "error" });
        return;
      }

      fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${encodeURIComponent(eldVehicle.vin)}?format=json`).then(response => response.json()).then((response) => {
        let make = "";
        let model = "";

        response.Results.forEach((result: { Variable: string; Value: string }) => {
          if (result.Variable === "Make") {
            make = result.Value || "";
          } else if (result.Variable === "Model") {
            model = result.Value || "";
          }
        });
        if (make || model) {
          selectedEldVehicleModel.update(() => {
            eldVehicle.vehicleMake = make;
            eldVehicle.vehicleModel = model;
          })
        } else {
          enqueueSnackbar("Make, Model and/or Year could not be decoded", { variant: "error" });
        }
      }).finally(() => {
        setLoadingVin(false);
      })
    }
  }
  const theme = useTheme();

  const { session, changeOrganization } = useAuthContext();

  const validVin = validateVIN(selectedEldVehicleModel.model?.vin || "");
  return selectedEldVehicleModel.model && <SimpleDialog
    title={selectedEldVehicleModel.isNew ? "New ELD Vehicle" : "Update ELD Vehicle"}
    open={isOpen}
    size="medium"
    onCancel={() => {
      setIsOpen(false);
    }}
    onSubmit={updateEldVehicle}
    submitDisabled={selectedEldVehicleModel.loading}
    submitButton={selectedEldVehicleModel.isNew ? "New ELD Vehicle" : "Update ELD Vehicle"}
    submitButtonIcon={selectedEldVehicleModel.isNew ? <Iconify icon="mingcute:add-line" /> : <Iconify icon="basil:save-solid" />}
    submitButtonColor={selectedEldVehicleModel.isNew ? "success" : "info"}>


    <DisplayErrors savingData={selectedEldVehicleModel} />

    <Stack direction="column" gap={1}>
      <TruxFormField label="Vehicle Number *">
        <TruxTextField modelSavingData={selectedEldVehicleModel} field="vehicleUnitNumber" />
      </TruxFormField>

      <TruxFormField label="Vin *">
        <Box display="flex" gap={2}>
          <Box flex={1}>
            <TruxTextField modelSavingData={selectedEldVehicleModel} field="vin" />
            {!validVin && <Box color={theme.palette.error.main} display="flex" alignItems="center" fontWeight="100"><Iconify icon="solar:danger-bold" width={24} /> Please enter a Valid VIN</Box>}
          </Box>
          <Box>
            <Button variant="soft" color="info" onClick={decodeVin}>
              {loadingVin && <CircularProgress size={16} sx={{ marginRight: "8px" }} />} Decode
            </Button>
          </Box>
        </Box>
      </TruxFormField>

      <TruxFormField label="Mac">
        <TruxTextField modelSavingData={selectedEldVehicleModel} field="mac" onChange={vehicle => {
          vehicle.mac = vehicle.mac.substring(0, Math.min(32, vehicle.mac.length));
          return false;
        }} />
      </TruxFormField>

      <TruxFormField label="Make">
        <TruxTextField modelSavingData={selectedEldVehicleModel} field="vehicleMake" />
      </TruxFormField>

      <TruxFormField label="Model">
        <TruxTextField modelSavingData={selectedEldVehicleModel} field="vehicleModel" />
      </TruxFormField>


      <TruxFormField label="License Plate *">
        <Box display="flex" gap={1}>
          <Box>
            <TruxTextField modelSavingData={selectedEldVehicleModel} field="licensePlate" />
          </Box>

          <Box>
            <TruxSelectField modelSavingData={selectedEldVehicleModel} field="licensePlateState" >
              <MenuItem value="" selected={!!selectedEldVehicleModel.model?.licensePlateState}>Please Select</MenuItem>
              {Constants.STATES.map((state) => <MenuItem value={state} key={state} selected={state === selectedEldVehicleModel.model?.licensePlateState}>{state}</MenuItem>)}
            </TruxSelectField>
          </Box>
        </Box>
      </TruxFormField>


      <TruxFormField label="Total Odometer">
        <TruxSimpleDecimalTwoField value={100 * selectedEldVehicleModel.model.totalMiles} onChange={(val) => {
          selectedEldVehicleModel.update(vehicle => {vehicle.totalMiles = val / 100});
        }} />
      </TruxFormField>

      <TruxFormField label="Total Engine Hours">
        <TruxSimpleDecimalTwoField value={100 * selectedEldVehicleModel.model.engineHours} onChange={(val) => {
          selectedEldVehicleModel.update(vehicle => {vehicle.engineHours = val / 100});
        }} />
      </TruxFormField>


      <TruxFormField label="Odometer Offset">
        <TruxSimpleDecimalTwoField allowNegative value={100 * (selectedEldVehicleModel.model.odometerOffset || 0)} onChange={(val) => {
          selectedEldVehicleModel.update(vehicle => {vehicle.odometerOffset = val / 100});
        }} />

        <Typography fontSize="small" fontStyle="italic">
          * only use this if your diagnostic odometer differs from physical
        </Typography>
      </TruxFormField>

      <TruxFormField label="Engine Hour Offset">
        <TruxSimpleDecimalTwoField allowNegative value={100 * (selectedEldVehicleModel.model.engineHourOffset || 0)} onChange={(val) => {
          selectedEldVehicleModel.update(vehicle => {vehicle.engineHourOffset = val / 100});
        }} />

        <Typography fontSize="small" fontStyle="italic">
          * only use this if your reported engine hours have an offset from physical
        </Typography>
      </TruxFormField>

      <TruxFormField label="Active">
        <TruxCheckboxField modelSavingData={selectedEldVehicleModel} field="active" />
      </TruxFormField>

      <TruxFormField label={
        <Box display="flex" alignItems="center" gap={0.75}>
          API Enabled
          <Tooltip title="If disabled, your integration partners will not be able to access this vehicle's information through the API." arrow>
            <Iconify width={16} icon="eva:info-outline" sx={{ color: 'text.disabled' }} />
          </Tooltip>
        </Box>
      }>
        <TruxCheckboxField modelSavingData={selectedEldVehicleModel} field="apiEnabled" />
      </TruxFormField>

      {session?.organization.id === 30 &&
        <TruxFormField label="Sim Based">
          <TruxSelectField modelSavingData={selectedEldVehicleModel} field="simBasedEldType" >
            <MenuItem value={0} selected={selectedEldVehicleModel.model?.simBasedEldType === 0}>None</MenuItem>
            <MenuItem value={1} selected={selectedEldVehicleModel.model?.simBasedEldType === 1}>Samsara</MenuItem>
            <MenuItem value={2} selected={selectedEldVehicleModel.model?.simBasedEldType === 2}>Geometris</MenuItem>
          </TruxSelectField>
        </TruxFormField>
      }

      {stats && <>
        {/* Keeping it simple for now */}
        {stats.coolantLevelPercent > 0 && <TruxFormField label="Coolant Level Percent">{formatAtMost2Decimals(stats.coolantLevelPercent)}%</TruxFormField>}
        {stats.coolantTemperature > 0 && <TruxFormField label="Coolant Temperature">{formatAtMost2Decimals(stats.coolantTemperature)}C</TruxFormField>}
        {stats.fuelLevelPercent > 0 && <TruxFormField label="Fuel Level Percent">{formatAtMost2Decimals(stats.fuelLevelPercent)}%</TruxFormField>}
        {stats.oilLevelPercent > 0 && <TruxFormField label="Oil Level Percent">{formatAtMost2Decimals(stats.oilLevelPercent)}%</TruxFormField>}
        {stats.oilTemperature > 0 && <TruxFormField label="Oil Temperature">{formatAtMost2Decimals(stats.oilTemperature)}C</TruxFormField>}
        {stats.dtcCodes?.length > 0 && <TruxFormField label="Diagnostic Codes">
          <Box sx={{pt: 0.55}}>
            <Violations violations={formatDtc(stats.dtcCodes)} />
          </Box>
        </TruxFormField>}
      </>}
    </Stack>
  </SimpleDialog >
}

export function formatDtc(dtcs: string[]): string[] {
  // Get unique values using Set
  const uniqueDtcs = Array.from(new Set(dtcs));

  // Map each unique DTC through the parser
  return uniqueDtcs.map(dtc => parseJ1939WithError(dtc));
}

const parseJ1939WithError = (dtc: string): string => {
  try {
      const value = parseInt(dtc, 16);

      if (Number.isNaN(value)) {
        return dtc;
      }

      // eslint-disable-next-line no-bitwise
      const spn = (value >>> 0) & 0x7FFFF;
      // eslint-disable-next-line no-bitwise
      const fmi = ((value >>> 0) >> 19) & 0x1F;

      return `SPN:${spn} | FMI:${fmi}`;
  } catch (error) {
      return dtc;
  }
};

export function formatAtMost2Decimals(val: number) {
  return Math.round(val * 100) / 100;
}